import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { SnackBarType } from '../../../../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../../../layout/SnackbarContext';
import {
  ConfirmationModal,
  ETIForm,
  SectionMetadataForm,
  UnsavedChangesModal,
} from '../components';
import { useETIForm } from '../hooks';
import styles from '../styles.module.scss';
import { ETI, ETIFormData, isETIV2 } from '../types';
import { ETITextParsingError, textToQuestions } from '../utils';

export interface ETIGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: ETIFormData) => Promise<boolean>;
  taskId: number;
  isLoading: boolean;
  eti: ETI | null;
}

const ETIGeneratorModal = ({
  isOpen,
  onClose,
  onSubmit,
  taskId,
  isLoading,
  eti,
}: ETIGeneratorModalProps) => {
  const { control, handleSubmit, reset, getValues, setValue, isDirty } =
    useETIForm();
  const { openSnack } = useContext(SnackbarContext);
  const isReadOnly = useMemo(
    () =>
      eti?.status === 'PUBLISHED' ||
      eti?.status === 'SUBMITTED' ||
      eti?.status === 'WAIVED',
    [eti],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDisabled = useMemo(
    () => isLoading || isSubmitting,
    [isLoading, isSubmitting],
  );
  const [publishConfirmData, setPublishConfirmData] =
    useState<ETIFormData | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const handleCloseAttempt = useCallback(() => {
    if (isDirty && !isReadOnly) {
      setShowUnsavedChangesModal(true);
      return;
    }
    reset({ questions: [], sectionMetadata: [] });
    setSelectedTab(0);
    onClose();
  }, [onClose, reset, isDirty, isReadOnly]);

  const handleForceClose = useCallback(() => {
    reset({ questions: [], sectionMetadata: [] });
    setSelectedTab(0);
    onClose();
  }, [onClose, reset]);

  const handleDiscardChanges = useCallback(() => {
    reset({ questions: [], sectionMetadata: [] });
    setSelectedTab(0);
    setShowUnsavedChangesModal(false);
    onClose();
  }, [onClose, reset]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      setSelectedTab(newValue);
    },
    [],
  );

  const handleResetForm = useCallback(() => {
    reset({ questions: [] });
  }, [reset]);

  const normalizeQuestions = useCallback(
    (
      formData: Omit<ETIFormData, 'taskId' | 'status'>,
    ): Omit<ETIFormData, 'taskId' | 'status'> => {
      const normalized = {
        ...formData,
        questions: formData.questions.map(q => {
          const { optionsRaw, filterRaw, ...rest } = q as any;

          let options: string[] | undefined;
          if (typeof optionsRaw === 'string' && optionsRaw.trim().length > 0) {
            try {
              const parsed = JSON.parse(optionsRaw);
              if (Array.isArray(parsed)) {
                options = parsed.map(v => String(v));
              }
            } catch (err) {
              console.error('Failed to parse optionsRaw', err);
            }
          } else if (Array.isArray(q.options)) {
            options = q.options;
          }

          let filter: typeof q.filter;
          if (typeof filterRaw === 'string' && filterRaw.trim().length > 0) {
            try {
              const parsedFilter = JSON.parse(filterRaw);
              if (parsedFilter && typeof parsedFilter === 'object') {
                filter = parsedFilter;
              }
            } catch (err) {
              console.error('Failed to parse filterRaw', err);
            }
          } else if (q.filter && typeof q.filter === 'object') {
            filter = q.filter;
          }

          // Only include options and filter if they have values
          const result: any = { ...rest };
          if (options !== undefined) {
            result.options = options;
          }
          if (filter !== undefined) {
            result.filter = filter;
          }

          return result;
        }),
      };
      return normalized;
    },
    [],
  );

  const handleOnSave = useCallback(
    async (formData: Omit<ETIFormData, 'taskId' | 'status'>) => {
      setIsSubmitting(true);
      try {
        const normalized = normalizeQuestions(formData);
        const success = await onSubmit({
          ...normalized,
          status: 'DRAFT',
          taskId,
        });
        if (success) {
          handleForceClose();
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [taskId, onSubmit, handleForceClose, normalizeQuestions],
  );

  const handleOnPublish = useCallback(
    (formData: Omit<ETIFormData, 'taskId' | 'status'>) =>
      setPublishConfirmData({
        ...normalizeQuestions(formData),
        status: 'PUBLISHED',
        taskId,
      }),
    [taskId, normalizeQuestions],
  );

  const handleOnPublishConfirm = useCallback(async () => {
    if (!publishConfirmData) return;
    setIsSubmitting(true);
    try {
      const success = await onSubmit(publishConfirmData);
      if (success) {
        handleForceClose();
        setPublishConfirmData(null);
      } else {
        setPublishConfirmData(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [publishConfirmData, handleForceClose, onSubmit]);

  const handleOnPaste = useCallback(
    (event: ClipboardEvent) => {
      if (!isOpen || isDisabled || isReadOnly || selectedTab !== 0) return;
      const pastedText = event.clipboardData?.getData('text');
      if (!pastedText) return;
      try {
        const questions = textToQuestions(pastedText);
        if (questions.length > 0) {
          const currentQuestions = getValues('questions');
          setValue('questions', [...currentQuestions, ...questions], {
            shouldDirty: true,
          });
          event.preventDefault();
        }
      } catch (error) {
        if (error instanceof ETITextParsingError) {
          event.preventDefault();
          openSnack({
            id: Date.now(),
            type: SnackBarType.ALERT,
            message: error.message,
            autoHideDuration: 6000,
          });
        } else {
          console.error('Failed to parse pasted ETI data', error);
        }
      }
    },
    [
      isOpen,
      getValues,
      isDisabled,
      isReadOnly,
      setValue,
      selectedTab,
      openSnack,
    ],
  );

  useEffect(() => {
    document.addEventListener('paste', handleOnPaste);
    return () => document.removeEventListener('paste', handleOnPaste);
  }, [handleOnPaste]);

  useEffect(() => {
    if (!eti || !isOpen) return;
    reset({
      questions: (eti.questions.questions || []).map(q => ({
        ...q,
        optionsRaw: q?.options ? JSON.stringify(q.options) : '',
        filterRaw: (q as any)?.filter ? JSON.stringify((q as any).filter) : '',
      })) as any,
      sectionMetadata: eti.questions.sectionMetadata || [],
    });
    setSelectedTab(0);
  }, [eti, isOpen, reset]);

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '80vw',
              maxHeight: '90vh',
            },
          },
        }}
        open={isOpen}
        onClose={isReadOnly || !isDirty ? handleCloseAttempt : undefined}
        disableEscapeKeyDown={!isReadOnly && isDirty}
      >
        <div className={styles.Header}>
          <DialogTitle
            sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <span>ETI</span>
            {eti && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Chip
                  size="small"
                  label={eti.status}
                  color={eti.status === 'PUBLISHED' ? 'success' : 'primary'}
                />
                {isDirty && !isReadOnly && eti.status === 'DRAFT' && (
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#000000',
                      flexShrink: 0,
                    }}
                    title="Unsaved changes"
                  />
                )}
              </div>
            )}
            <Box sx={{ marginLeft: 'auto', marginRight: 3 }}>
              {/* Version 2 is the new ETI with surveyJS schema */}
              {eti &&
                isETIV2(eti) &&
                eti.questions.questions.length > 0 &&
                eti.status !== 'SUBMITTED' && (
                  <Button
                    color="primary"
                    variant="outlined"
                    disabled={isDisabled}
                    onClick={() =>
                      window.open(`/workbench/eti/${taskId}/preview`, '_blank')
                    }
                  >
                    Preview
                  </Button>
                )}
              {eti && isETIV2(eti) && eti.status === 'SUBMITTED' && (
                <Button
                  color="primary"
                  variant="outlined"
                  disabled={isDisabled}
                  onClick={() =>
                    window.open(`/workbench/eti/${taskId}/answer`, '_blank')
                  }
                >
                  View Response
                </Button>
              )}
            </Box>
          </DialogTitle>
        </div>
        <IconButton
          aria-label="close"
          onClick={handleCloseAttempt}
          sx={closeIconStyle}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            paddingBlock: 0,
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={selectedTab} onChange={handleTabChange}>
              <Tab label="Questions" />
              <Tab label="Section Details" />
            </Tabs>
          </Box>
          {selectedTab === 0 && (
            <ETIForm
              isLoading={isLoading}
              isReadOnly={isReadOnly}
              isDisabled={isDisabled}
              control={control as any}
              setValue={setValue as any}
              answers={eti?.answers?.answers ?? []}
              isVersion2={isETIV2(eti)}
              onReset={handleResetForm}
            />
          )}
          {selectedTab === 1 && (
            <SectionMetadataForm
              isLoading={isLoading}
              isReadOnly={isReadOnly}
              isDisabled={isDisabled}
              control={control as any}
            />
          )}
        </DialogContent>
        {!isReadOnly && (
          <DialogActions>
            <Button
              color="primary"
              disabled={isDisabled}
              onClick={handleSubmit(handleOnSave)}
            >
              Save
            </Button>
            <Button
              color="error"
              disabled={isDisabled}
              onClick={handleSubmit(handleOnPublish)}
            >
              Publish
            </Button>
          </DialogActions>
        )}
      </Dialog>
      <ConfirmationModal
        isOpen={!!publishConfirmData}
        onClose={() => setPublishConfirmData(null)}
        onSubmit={handleOnPublishConfirm}
      />
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onClose={() => setShowUnsavedChangesModal(false)}
        onDiscard={handleDiscardChanges}
      />
    </>
  );
};

const closeIconStyle = (theme: Theme) => ({
  position: 'absolute',
  right: 8,
  top: 8,
  color: theme.palette.grey[500],
});

export default ETIGeneratorModal;
